import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import {
  X, Gauge, Fuel, Cog, Car as CarIcon, Calendar, Zap, Settings2, Palette,
  DoorOpen, Users, Hash, BadgeCheck, MessageCircle, Ship, Check, Loader2, ChevronLeft, ChevronRight,
} from "lucide-react";
import api, { WHATSAPP_NUMBER, formatApiErrorDetail } from "../../lib/api";

const SpecRow = ({ icon: Icon, label, value }) =>
  value ? (
    <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-3" data-testid={`spec-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon size={18} className="shrink-0 text-navy" />
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="truncate text-sm font-bold text-navy">{value}</p>
      </div>
    </div>
  ) : null;

export const CarDetailModal = ({ car, onClose }) => {
  const [idx, setIdx] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  if (!car) return null;
  const sold = car.status === "sold";
  const gallery = [...new Set([car.image_url, ...(car.images || [])].filter(Boolean))];
  const main = gallery[idx] || car.image_url;
  const features = car.features || [];
  const waLink = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    `Hello R.O.K Motors, I'm interested in the ${car.title} (${car.price}). Is it still available?`
  )}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    setSending(true);
    try {
      await api.post("/car-enquiry", {
        ...form, car_title: car.title, car_price: car.price,
      });
      setDone(true);
      toast.success("Request sent! Our team will contact you shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto bg-black/70 p-0 sm:p-6"
        onClick={onClose}
        data-testid="car-detail-modal"
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
          onClick={(e) => e.stopPropagation()}
          className="relative my-0 w-full max-w-6xl bg-white shadow-2xl sm:my-4 sm:rounded-3xl"
        >
          <button
            onClick={onClose}
            data-testid="car-detail-close"
            className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-navy shadow-lg backdrop-blur transition hover:bg-white"
          >
            <X size={20} />
          </button>

          <div className="grid gap-0 lg:grid-cols-[1.5fr_1fr]">
            {/* LEFT: gallery + details */}
            <div className="p-4 sm:p-6">
              {/* Gallery */}
              <div className="relative overflow-hidden rounded-2xl bg-navy-50">
                <img src={main} alt={car.title} className={`h-64 w-full object-cover sm:h-96 ${sold ? "grayscale" : ""}`} data-testid="car-detail-main-image" />
                {gallery.length > 1 && (
                  <>
                    <button onClick={() => setIdx((i) => (i - 1 + gallery.length) % gallery.length)} className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-navy shadow hover:bg-white" data-testid="gallery-prev"><ChevronLeft size={20} /></button>
                    <button onClick={() => setIdx((i) => (i + 1) % gallery.length)} className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-navy shadow hover:bg-white" data-testid="gallery-next"><ChevronRight size={20} /></button>
                  </>
                )}
                {car.featured && !sold && <span className="absolute left-3 top-3 rounded-full bg-gold px-3 py-1 text-xs font-bold text-navy shadow">Featured</span>}
                {sold && <span className="absolute left-3 top-3 rounded-lg bg-brandred px-4 py-1.5 font-display text-sm font-black text-white">SOLD</span>}
              </div>
              {gallery.length > 1 && (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="car-detail-thumbs">
                  {gallery.map((g, i) => (
                    <button key={i} onClick={() => setIdx(i)} className={`h-16 w-24 shrink-0 overflow-hidden rounded-lg ring-2 transition ${i === idx ? "ring-brandred" : "ring-transparent hover:ring-navy/30"}`}>
                      <img src={g} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Technical data */}
              <h3 className="mt-6 font-display text-lg font-black text-navy">Technical Data</h3>
              <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                <SpecRow icon={Gauge} label="Mileage" value={car.mileage} />
                <SpecRow icon={Calendar} label="First Reg." value={car.first_registration || car.year} />
                <SpecRow icon={Cog} label="Transmission" value={car.transmission} />
                <SpecRow icon={Fuel} label="Fuel" value={car.fuel_type} />
                <SpecRow icon={Zap} label="Power" value={car.power} />
                <SpecRow icon={Settings2} label="Engine" value={car.engine} />
                <SpecRow icon={CarIcon} label="Body" value={car.body_type} />
                <SpecRow icon={Palette} label="Color" value={car.exterior_color} />
                <SpecRow icon={DoorOpen} label="Doors" value={car.doors} />
                <SpecRow icon={Users} label="Seats" value={car.seats} />
                <SpecRow icon={BadgeCheck} label="Condition" value={car.condition} />
                <SpecRow icon={Hash} label="VIN" value={car.vin} />
              </div>

              {features.length > 0 && (
                <>
                  <h3 className="mt-6 font-display text-lg font-black text-navy">Features & Equipment</h3>
                  <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2" data-testid="car-detail-features">
                    {features.map((f, i) => (
                      <span key={i} className="inline-flex items-center gap-2 text-sm text-navy">
                        <Check size={15} className="shrink-0 text-green-600" /> {f}
                      </span>
                    ))}
                  </div>
                </>
              )}

              {car.description && (
                <>
                  <h3 className="mt-6 font-display text-lg font-black text-navy">Description</h3>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{car.description}</p>
                </>
              )}
            </div>

            {/* RIGHT: price + CTAs */}
            <div className="border-t border-gray-100 bg-navy-50/40 p-4 sm:p-6 lg:border-l lg:border-t-0">
              <div className="lg:sticky lg:top-6">
                <p className="text-xs uppercase tracking-widest text-gold-700">R.O.K Motors Auto Sales</p>
                <h2 className="mt-1 font-display text-2xl font-black leading-tight text-navy" data-testid="car-detail-title">{car.title}</h2>
                <p className="mt-3 font-display text-3xl font-black text-brandred" data-testid="car-detail-price">{car.price}</p>
                <p className="mt-1 text-xs text-muted-foreground">Price excludes shipping & customs to Ghana — ask us for an all-in quote.</p>

                {!showForm && !done && (
                  <div className="mt-5 space-y-2.5">
                    <a href={sold ? undefined : waLink} target="_blank" rel="noreferrer" data-testid="car-detail-whatsapp"
                      className={`flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white transition active:scale-95 ${sold ? "cursor-not-allowed bg-gray-300" : "bg-[#25D366] hover:brightness-95"}`}>
                      <MessageCircle size={17} /> {sold ? "Sold Out" : "Enquire on WhatsApp"}
                    </a>
                    <button onClick={() => setShowForm(true)} disabled={sold} data-testid="car-detail-request-quote"
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-navy px-4 py-3 text-sm font-bold text-white transition hover:bg-navy-700 active:scale-95 disabled:opacity-50">
                      <Ship size={17} /> Request Shipping Quote
                    </button>
                  </div>
                )}

                {showForm && !done && (
                  <form onSubmit={submit} className="mt-5 space-y-2.5" data-testid="car-enquiry-form">
                    <p className="text-sm font-bold text-navy">Get an all-in quote to Ghana</p>
                    <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="enquiry-name" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-navy" />
                    <input required placeholder="Phone / WhatsApp" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="enquiry-phone" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-navy" />
                    <input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="enquiry-email" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-navy" />
                    <textarea rows={2} placeholder="Message (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="enquiry-message" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-navy" />
                    <button type="submit" disabled={sending} data-testid="enquiry-submit" className="flex w-full items-center justify-center gap-2 rounded-xl bg-brandred px-4 py-3 text-sm font-bold text-white hover:bg-brandred-700 disabled:opacity-60">
                      {sending ? <Loader2 size={16} className="animate-spin" /> : <Ship size={16} />} Send Request
                    </button>
                    <button type="button" onClick={() => setShowForm(false)} className="w-full text-xs font-semibold text-muted-foreground hover:text-navy">Cancel</button>
                  </form>
                )}

                {done && (
                  <div className="mt-5 rounded-xl bg-green-50 p-4 text-center" data-testid="enquiry-success">
                    <Check className="mx-auto mb-1 text-green-600" size={28} />
                    <p className="text-sm font-bold text-navy">Request received!</p>
                    <p className="text-xs text-muted-foreground">Our team will reach out with an all-in shipping quote shortly.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
